"use client";

import { useTranslation } from "@guru/shared/i18n";
import { cn } from "@guru/shared/utils/cn";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { FeatureGlyph, type GlyphName, type GlyphTone } from "@/components/marketing/feature-glyph";
import { SpotlightCard } from "@/components/marketing/spotlight-card";
import { ScrollReveal } from "@/components/public/scroll-reveal";

const LIVE_STATUSES = new Set(["Live Now", "ഇപ്പോൾ ലൈവ്"]);
const BUILDING_STATUSES = new Set(["In Development", "വികസനത്തിലാണ്"]);

function toneFor(status: string): GlyphTone {
  if (LIVE_STATUSES.has(status)) return "live";
  if (BUILDING_STATUSES.has(status)) return "building";
  return "planned";
}

export function RoadmapSection() {
  const { t } = useTranslation();

  return (
    <section id="roadmap" className="relative overflow-clip bg-background py-20 sm:py-24">
      {/* Ambient ledger texture anchors the section in the product's world */}
      <div
        className="ledger-grid ledger-grid-fade pointer-events-none absolute inset-0"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <ScrollReveal className="mb-12 text-center sm:mb-16" threshold={0.3}>
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-deep">
            Built in the open
          </p>
          <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t.homepage.roadmap.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-lg text-muted-foreground">
            {t.homepage.roadmap.subtitle}
          </p>
        </ScrollReveal>

        {/* Roadmap Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {t.homepage.roadmap.phases.map((phase, index) => {
            const tone = toneFor(phase.status);
            const isLive = tone === "live";

            return (
              <ScrollReveal key={phase.title} delay={index * 100} threshold={0.2}>
                <SpotlightCard
                  className={cn(
                    "group flex h-full flex-col rounded-2xl border p-4",
                    "transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl",
                    isLive
                      ? "border-positive/40 bg-positive/[0.04] hover:border-positive"
                      : "border-border bg-card hover:border-brand/50"
                  )}
                >
                  {/* Animated illustration */}
                  <FeatureGlyph name={phase.glyph as GlyphName} tone={tone} />

                  <div className="flex flex-1 flex-col px-1 pt-4">
                    {/* Phase Label */}
                    <span
                      className={cn(
                        "text-xs font-semibold uppercase tracking-wider",
                        isLive ? "text-positive" : "text-brand-deep"
                      )}
                    >
                      {phase.phase}
                    </span>

                    {/* Title */}
                    <h3 className="mt-1.5 text-lg font-semibold text-foreground">{phase.title}</h3>

                    {/* Description */}
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {phase.description}
                    </p>

                    {/* Status Badge */}
                    <span
                      className={cn(
                        "mt-4 inline-block w-fit rounded-full px-2.5 py-1 text-xs font-medium",
                        isLive
                          ? "badge-flip bg-positive/15 text-positive"
                          : tone === "building"
                            ? "bg-brand/15 text-brand-deep"
                            : "bg-muted text-muted-foreground"
                      )}
                    >
                      {phase.status}
                    </span>
                  </div>
                </SpotlightCard>
              </ScrollReveal>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            href="/roadmap"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-deep underline-offset-4 hover:underline"
          >
            See the full roadmap — what's live, what's next
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
